import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Sparkles, Loader2 } from "lucide-react";

export const Briefings = () => {
  const qc = useQueryClient();
  const [scope, setScope] = useState("daily");
  const [activeBrief, setActiveBrief] = useState(null);

  const types = useQuery({ queryKey: ["brief-types"], queryFn: async () => (await api.get("/briefings/types")).data });
  const list = useQuery({ queryKey: ["brief-list"], queryFn: async () => (await api.get("/briefings")).data });

  const gen = useMutation({
    mutationFn: async () => (await api.post("/briefings/generate", { scope })).data,
    onSuccess: (b) => {
      toast.success(`${b.title} generated`);
      setActiveBrief(b);
      qc.invalidateQueries({ queryKey: ["brief-list"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="page-briefings">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Executive Intelligence Briefings</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
            <Sparkles className="text-[#C99A2E]"/> Briefing Library
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">Daily, weekly, monthly and ministerial briefs — AI-narrated, digitally signed and print-ready.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="brief-scope"
                  className="bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2 text-[13px] text-[#F6F8FA] min-w-[220px]">
            {(types.data || []).map((t) => <option key={t.scope} value={t.scope}>{t.title}</option>)}
          </select>
          <button onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="brief-generate"
                  className="cad-btn-primary px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2">
            {gen.isPending ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Generate
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 cad-panel p-6" data-testid="brief-active">
          {!activeBrief && !list.data?.length && (
            <div className="text-[13px] text-[#B3C2CC]">Select a briefing type above and click Generate. The Sovereign Intelligence Engine will compose an executive-grade narrative and produce a digitally-signed PDF.</div>
          )}
          {activeBrief && (
            <>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">{activeBrief.audience}</div>
                  <div className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">{activeBrief.title}</div>
                  <div className="font-mono-cad text-[10px] text-[#7C93A5] mt-1">
                    Generated {activeBrief.generated_at.slice(0, 19).replace("T"," ")} · Cadence {activeBrief.cadence}
                  </div>
                </div>
                <a href={`${API}/briefings/${activeBrief.id}.pdf`} target="_blank" rel="noopener noreferrer"
                   data-testid="brief-pdf"
                   className="cad-btn-outline px-3 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
                  <FileDown size={14}/> PDF
                </a>
              </div>
              <div className="mt-4 space-y-3 text-[14px] text-[#F6F8FA] leading-relaxed">
                {activeBrief.narrative.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5] mb-2">Recent briefings ({list.data?.length || 0})</div>
          <div className="space-y-2">
            {(list.data || []).map((b) => (
              <button key={b.id} onClick={() => setActiveBrief(b)} data-testid={`brief-item-${b.id}`}
                      className="w-full text-left border cad-hairline rounded-sm p-3 hover:bg-[#145A7A]/25">
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-display font-bold text-[#F6F8FA]">{b.title}</span>
                  <span className="cad-chip cad-chip-gold text-[10px]">{b.scope}</span>
                </div>
                <div className="text-[11px] font-mono-cad text-[#7C93A5] mt-1">{b.audience} · {b.generated_at.slice(0, 10)}</div>
              </button>
            ))}
            {(!list.data || list.data.length === 0) && <div className="text-[12px] text-[#7C93A5] italic">No briefings yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
